import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore, API_BASE, apiFetch } from '../store/auth'
import {
  Button,
  PageLoading,
  PageHeader,
  Card,
  NoOrganizationMessage,
  Input,
  FormField,
  Toggle,
} from '../components'

interface ReminderSettings {
  organizationId: string
  enabled: boolean
  firstReminderDays: number
  secondReminderDays: number
  finalReminderDays: number
  firstReminderFee: number
  secondReminderFee: number
  finalReminderFee: number
  emailSubjectTemplate: string
  emailBodyTemplate: string | null
  sendCopyToOrg: boolean
}

const defaultSettings: ReminderSettings = {
  organizationId: '',
  enabled: true,
  firstReminderDays: 7,
  secondReminderDays: 1,
  finalReminderDays: 14,
  firstReminderFee: 0,
  secondReminderFee: 500,
  finalReminderFee: 500,
  emailSubjectTemplate: 'Maksumuistutus - Lasku {{invoice_number}}',
  emailBodyTemplate: null,
  sendCopyToOrg: false,
}

// Format cents to euros
function formatEuroInput(cents: number): string {
  return (cents / 100).toFixed(2)
}

// Parse euros to cents
function parseEuroInput(value: string): number {
  const euros = parseFloat(value.replace(',', '.'))
  return isNaN(euros) ? 0 : Math.round(euros * 100)
}

export default function InvoiceSettingsPage() {
  const { t } = useTranslation()
  const { activeOrg } = useAuthStore()
  const [settings, setSettings] = useState<ReminderSettings>(defaultSettings)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    if (!activeOrg) return
    setLoading(true)
    setError('')
    try {
      const response = await apiFetch(
        `${API_BASE}/invoices/reminder-settings?organization_id=${activeOrg.id}`
      )
      if (!response.ok) throw new Error('Asetusten lataus epäonnistui')

      const data = await response.json()
      if (data.settings) {
        setSettings(data.settings)
      }
    } catch {
      setError('Asetusten lataus epäonnistui')
    } finally {
      setLoading(false)
    }
  }, [activeOrg])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  // Save settings
  const handleSave = async () => {
    if (!activeOrg) return
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      const response = await apiFetch(`${API_BASE}/invoices/reminder-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: activeOrg.id,
          enabled: settings.enabled,
          first_reminder_days: settings.firstReminderDays,
          second_reminder_days: settings.secondReminderDays,
          final_reminder_days: settings.finalReminderDays,
          first_reminder_fee: settings.firstReminderFee,
          second_reminder_fee: settings.secondReminderFee,
          final_reminder_fee: settings.finalReminderFee,
          email_subject_template: settings.emailSubjectTemplate,
          email_body_template: settings.emailBodyTemplate,
          send_copy_to_org: settings.sendCopyToOrg,
        }),
      })

      if (!response.ok) throw new Error('Asetusten tallennus epäonnistui')
      setSuccess('Asetukset tallennettu')
    } catch {
      setError('Asetusten tallennus epäonnistui')
    } finally {
      setSaving(false)
    }
  }

  if (!activeOrg) {
    return <NoOrganizationMessage />
  }

  if (loading) return <PageLoading />

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Laskujen asetukset"
        subtitle={`${activeOrg.name} • Maksumuistutusten asetukset`}
      />

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">{error}</div>
      )}

      {success && (
        <div className="bg-green-50 text-green-600 p-4 rounded-lg">{success}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Main Settings */}
        <Card title="Muistutusten yleisasetukset">
          <div className="space-y-4">
            <Toggle
              label="Automaattiset maksumuistutukset käytössä"
              checked={settings.enabled}
              onChange={(checked: boolean) =>
                setSettings((s) => ({ ...s, enabled: checked }))
              }
            />

            <Toggle
              label="Lähetä kopio muistutuksista organisaation sähköpostiin"
              checked={settings.sendCopyToOrg}
              onChange={(checked: boolean) =>
                setSettings((s) => ({ ...s, sendCopyToOrg: checked }))
              }
            />

            <FormField label="Sähköpostin otsikko">
              <Input
                type="text"
                value={settings.emailSubjectTemplate}
                onChange={(e) =>
                  setSettings((s) => ({
                    ...s,
                    emailSubjectTemplate: e.target.value,
                  }))
                }
                placeholder="Maksumuistutus - Lasku {{invoice_number}}"
              />
              <p className="text-sm text-gray-500 mt-1">
                Käytä muuttujia: {'{{invoice_number}}'}, {'{{customer_name}}'},{' '}
                {'{{due_date}}'}, {'{{amount}}'}
              </p>
            </FormField>
          </div>
        </Card>

        {/* Reminder Schedule */}
        <Card title="Muistutusaikataulu">
          <div className="space-y-6">
            {/* First reminder */}
            <div className="border-b pb-4">
              <h4 className="font-medium mb-3">1. muistutus (ennen eräpäivää)</h4>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Päivää ennen eräpäivää">
                  <Input
                    type="number"
                    min={0}
                    value={settings.firstReminderDays}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        firstReminderDays: parseInt(e.target.value) || 0,
                      }))
                    }
                  />
                </FormField>
                <FormField label="Muistutusmaksu (€)">
                  <Input
                    type="text"
                    value={formatEuroInput(settings.firstReminderFee)}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        firstReminderFee: parseEuroInput(e.target.value),
                      }))
                    }
                  />
                </FormField>
              </div>
            </div>

            {/* Second reminder */}
            <div className="border-b pb-4">
              <h4 className="font-medium mb-3">2. muistutus (eräpäivän jälkeen)</h4>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Päivää eräpäivän jälkeen">
                  <Input
                    type="number"
                    min={0}
                    value={settings.secondReminderDays}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        secondReminderDays: parseInt(e.target.value) || 0,
                      }))
                    }
                  />
                </FormField>
                <FormField label="Muistutusmaksu (€)">
                  <Input
                    type="text"
                    value={formatEuroInput(settings.secondReminderFee)}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        secondReminderFee: parseEuroInput(e.target.value),
                      }))
                    }
                  />
                </FormField>
              </div>
            </div>

            {/* Final reminder */}
            <div>
              <h4 className="font-medium mb-3">Viimeinen muistutus</h4>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Päivää eräpäivän jälkeen">
                  <Input
                    type="number"
                    min={0}
                    value={settings.finalReminderDays}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        finalReminderDays: parseInt(e.target.value) || 0,
                      }))
                    }
                  />
                </FormField>
                <FormField label="Muistutusmaksu (€)">
                  <Input
                    type="text"
                    value={formatEuroInput(settings.finalReminderFee)}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        finalReminderFee: parseEuroInput(e.target.value),
                      }))
                    }
                  />
                </FormField>
              </div>
            </div>
          </div>
        </Card>

        {/* Email Template */}
        <Card title="Sähköpostipohja (valinnainen)" className="lg:col-span-2">
          <FormField label="HTML-sähköpostipohja">
            <textarea
              className="w-full h-64 px-3 py-2 border rounded-lg font-mono text-sm"
              value={settings.emailBodyTemplate || ''}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  emailBodyTemplate: e.target.value || null,
                }))
              }
              placeholder="Jätä tyhjäksi käyttääksesi oletuspohjaa"
            />
          </FormField>
          <div className="mt-4 text-sm text-gray-500">
            <p className="font-medium">Käytettävissä olevat muuttujat:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>
                <code>{'{{invoice_number}}'}</code> - Laskun numero
              </li>
              <li>
                <code>{'{{customer_name}}'}</code> - Asiakkaan nimi
              </li>
              <li>
                <code>{'{{due_date}}'}</code> - Eräpäivä
              </li>
              <li>
                <code>{'{{amount}}'}</code> - Laskun summa
              </li>
              <li>
                <code>{'{{reminder_fee}}'}</code> - Tämän muistutuksen maksu
              </li>
              <li>
                <code>{'{{total_amount}}'}</code> - Kokonaissumma maksuineen
              </li>
              <li>
                <code>{'{{reminder_type}}'}</code> - Muistutuksen tyyppi
              </li>
            </ul>
          </div>
        </Card>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} variant="primary">
          {saving ? t('common.saving') : t('common.saveSettings')}
        </Button>
      </div>
    </div>
  )
}
